/**
 * Standalone component that renders a dropdown menu with imbedded logic to
 * tailor different menu items (links) based on the profile of the user.
 */

import {t} from "@lingui/core/macro";

import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {ActionItem, ActionMenu} from "@khanacademy/wonder-blocks-dropdown";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {isCountryBlocked} from "@khan/khanmigo-access";
import {RequestInfo} from "@khan/shared-core";
import {parseUrl, urlInLanguage} from "@khan/urls";

import type {ProfileModelType} from "#shared-package/profile-model.ts";

import KhanmigoActivitiesClickable from "./khanmigo-activities-clickable.tsx";
import {PARENT_KHANMIGO_TOOLS_PATH} from "./util.ts";

import externalLinkIcon from "@phosphor-icons/core/regular/arrow-square-out.svg";

type Props = {
    profile: ProfileModelType | null;
    navItemStyles: StyleType;
    isOpen: boolean;
    onToggle: (opened: boolean) => void;
};

type OptionSelected =
    | "LEARNER_ACTIVITIES"
    | "PARENT_TOOLS"
    | "TEACHER_TOOLS"
    | "MY_DOCUMENTS"
    | "WRITING_COACH";

export const KhanmigoActivitiesDropdown = ({
    profile,
    navItemStyles,
    isOpen,
    onToggle,
}: Props): React.ReactElement | null => {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    const teacherToolsHref = "/teacher/khanmigo-tools";
    const parentHref = PARENT_KHANMIGO_TOOLS_PATH;
    const learnerHref =
        "/profile/me/khanmigo/activities/activity-default-learner";
    const documentHref = "/teacher/resources/documents";
    const khanmigoHref = "/profile/me/khanmigo/activities";
    const writingCoachHref = "/writing-coach";
    const adminToolsHref = "/districts/khanmigo-activities";
    const enWritingCoachHref = urlInLanguage("en", parseUrl("/writing-coach"));

    const isParent = !!profile?.get("hasAccessToAIGuideParent");
    const isLearner = !!profile?.get("hasAccessToAIGuideLearner");
    const isTeacher = !!profile?.get("hasAccessToAIGuideTeacher");
    const isDistrictAdmin = !!profile?.get("hasAccessToAIGuideDistrictAdmin");
    const isDistrictLearner =
        isLearner && profile?.get("includesDistrictOwnedData");

    const isEN = RequestInfo.kaLocale === "en";

    const khanmigoButtonClickedEvent = (
        optionSelected: OptionSelected | "NONE",
    ) =>
        publishAnalyticsEvent({
            eventType: "GlobalNavBarAiGuideDropdownClick",
            eventSchemaVersion: 2,
            optionSelected,
            isAiEnabledParent: isParent,
            isAiEnabledFullAccessTeacher: isTeacher && isLearner,
            isAiEnabledLimitedTeacher: isTeacher && !isLearner,
        });

    const dropdownOptionSelectedEvent = (optionSelected: OptionSelected) => {
        publishAnalyticsEvent({
            eventType: "GlobalNavBarAiGuideDropdownOptionSelected",
            eventSchemaVersion: 2,
            optionSelected,
            isAiEnabledParent: isParent,
            isAiEnabledFullAccessTeacher: isTeacher && isLearner,
            isAiEnabledLimitedTeacher: isTeacher && !isLearner,
        });
    };

    const parentToolsSelectedEvent = (): void => {
        publishAnalyticsEvent({
            eventType: "AiParentToolsUserNavigated",
            eventSchemaVersion: 1,
            navigationElement: "PARENT_TOOLS_DROPDOWN",
        });
    };

    // If user is a learner, but not a teacher, parent, nor admin,
    // show AI activities button
    if (isLearner && !isParent && !isTeacher && !isDistrictLearner) {
        return (
            <KhanmigoActivitiesClickable
                navItemStyles={navItemStyles}
                href={khanmigoHref}
                role="link"
                onClick={() => khanmigoButtonClickedEvent("LEARNER_ACTIVITIES")}
            />
        );
    }
    if (isLearner || isParent || isTeacher) {
        return (
            <ActionMenu
                menuText="Khanmigo"
                testId="khanmigo-dropdown"
                opener={(openerProps) => (
                    // eslint-disable-next-line jsx-a11y/role-supports-aria-props -- TODO: Address a11y error
                    <KhanmigoActivitiesClickable
                        navItemStyles={navItemStyles}
                        role="menu"
                        aria-expanded={isOpen}
                        onClick={() => khanmigoButtonClickedEvent("NONE")}
                        isOpen={openerProps.opened}
                    />
                )}
                onToggle={onToggle}
                style={{height: "100%", padding: "0 16px"}}
                dropdownStyle={{top: "unset"}}
            >
                {/* Learners only */}
                {isLearner && (
                    <ActionItem
                        label={t`Learner activities`}
                        href={learnerHref}
                        testId="learner-activities"
                        onClick={() =>
                            dropdownOptionSelectedEvent("LEARNER_ACTIVITIES")
                        }
                    />
                )}
                {/* Parents only */}
                {isParent && (
                    <ActionItem
                        label={t`Parent tools`}
                        href={parentHref}
                        testId="parent-tools"
                        onClick={() => {
                            dropdownOptionSelectedEvent("PARENT_TOOLS");
                            parentToolsSelectedEvent();
                        }}
                    />
                )}
                {/* Teacher / Admin / WHAM */}
                {isTeacher && (
                    <ActionItem
                        label={t`Teacher tools`}
                        href={teacherToolsHref}
                        testId="teacher-tools"
                        onClick={() =>
                            dropdownOptionSelectedEvent("TEACHER_TOOLS")
                        }
                    />
                )}
                {isDistrictAdmin && (
                    <ActionItem
                        label={t`Admin tools`}
                        href={adminToolsHref}
                        testId="admin-tools"
                    />
                )}
                {isTeacher && (
                    <ActionItem
                        label={t`My documents`}
                        href={documentHref}
                        testId="document-tools"
                        onClick={() =>
                            dropdownOptionSelectedEvent("MY_DOCUMENTS")
                        }
                    />
                )}
                {(isDistrictLearner || isTeacher) &&
                    !isCountryBlocked(RequestInfo.remoteCountry) && (
                        <ActionItem
                            href={isEN ? writingCoachHref : enWritingCoachHref}
                            label={
                                isEN
                                    ? t`Writing Coach`
                                    : t`Writing Coach (English only)`
                            }
                            onClick={() =>
                                dropdownOptionSelectedEvent("WRITING_COACH")
                            }
                            rightAccessory={
                                <PhosphorIcon
                                    icon={externalLinkIcon}
                                    size="small"
                                />
                            }
                            target="_blank"
                            testId="writing-coach"
                        />
                    )}
            </ActionMenu>
        );
    }
    // If a user has no relevant permissions, do not render a link.
    // This is possible if, for example, a user enrolls in free teacher
    // khanmigo and then removes the teacher role.
    return null;
};
